<template>
  <div>
    <!--    title-->
    <v-row
      style="border-bottom: 1px solid #E5E7EB;"
      class="margin-0"
    >
      <v-col
        class="d-flex justify-start block-title"
      >
        <div
          class="block-title--wrapper"
        >
          {{
            item.insurance_provider || defaultEmptyValue
          }}
        </div>
      </v-col>

      <v-col
        class="d-flex justify-end"
      >
        <active-icon
          :cell-data="item.status"
          class="mr-2"
        />

        <table-row-actions-menu
          :data-index="itemIndex"
          :config="actionsConfig"
          :table-data="data"
        />
      </v-col>
    </v-row>

    <!--    visible data section-->
    <v-row
      v-for="(dataObject, dataObjIndex) in getVisibleItemDetails()"
      :key="dataObjIndex"
      class="margin-0"
    >
      <v-col
        class="data-label"
      >
        {{ dataObject.label }}
      </v-col>

      <v-col
        class="d-flex justify-end data-value"
      >
        {{ dataObject.value }}
      </v-col>
    </v-row>

    <!--    hidden data section-->
    <template
      v-if="slidedDown"
    >
      <v-row
        v-for="(dataObject, dataObjIndex) in getAllItemDetails()"
        :key="'hidden_' + dataObjIndex"
        class="margin-0"
      >
        <v-col
          class="data-label"
        >
          {{ dataObject.label }}
        </v-col>

        <v-col
          class="d-flex justify-end data-value"
        >
          <template
            v-if="dataObject.key === 'images' || dataObject.key === 'pdf'"
          >
            <table-image-icon-with-counter
              :cell-data="dataObject.value"
              :config="dataObject"
              :row-data="item"
            />
          </template>
          <template
            v-else-if="dataObject.key === 'alarm_on'"
          >
            <cell-icon
              :cell-data="dataObject.value"
              :config="dataObject"
              :row-data="item"
            />
          </template>
          <template
            v-else
          >
            {{ dataObject.value }}
          </template>
        </v-col>
      </v-row>
    </template>

    <!--    arrow up/down button for showing more details-->
    <image-field
      :src="slidedDown ? '/img/icons/arrow-up-base.svg' : '/img/icons/arrow-down-base.svg'"
      class="arrowUpDown"
      @onClick="slidedDown = !slidedDown"
    />

    <information-dialog
      :open-dialog="informationDialogConfig.openDialog"
      :title="informationDialogConfig.title"
      :dialog-message="informationDialogConfig.message"
      :positive-button-text="informationDialogConfig.positiveBtnText"
      :negative-button-text="informationDialogConfig.negativeBtnText"
      :confirm-callback-function="informationDialogConfig.callbackFn"
      @close="$set(informationDialogConfig, 'openDialog', false)"
    />

    <images-preview-dialog
      v-if="imagesPreviewDialogConfig.show"
      :show-dialog="imagesPreviewDialogConfig.show"
      :dialog-title="imagesPreviewDialogConfig.title"
      :images="imagesPreviewDialogConfig.images"
      :field="imagesPreviewDialogConfig.field"
      :remove-callback="imagesPreviewDialogConfig.removeCallback"
      :remove-api-config="imagesPreviewDialogConfig.removeApiConfig"
      @close="imagesPreviewDialogConfig.show = false"
    />

    <files-preview-dialog
      v-if="filesPreviewDialogConfig.show"
      :dialog-title="filesPreviewDialogConfig.title"
      :field="filesPreviewDialogConfig.field"
      :show-dialog="filesPreviewDialogConfig.show"
      :files="filesPreviewDialogConfig.files"
      :remove-callback="filesPreviewDialogConfig.removeCallback"
      :remove-api-config="filesPreviewDialogConfig.removeApiConfig"
      @close="filesPreviewDialogConfig.show = false"
    />
  </div>
</template>

<script>
import ImageField from '@/global/components/fields/ImageField.vue'
import ActiveIcon from '../../components/main-table/ActiveIcon.vue'
import TableRowActionsMenu from '@/global/components/table/components/TableRowActionsMenu.vue'
import InformationDialog from '@/global/components/dialogs/InformationDialog.vue'
import { dateService, FORMATS } from '@/global/services/date'
import TableImageIconWithCounter from '../../components/TableImageIconWithCounter.vue'
import CellIcon from '../../components/main-table/CellIcon.vue'
import previewFiles from '../../helpers/previewFiles'
import ImagesPreviewDialog from '@/global/components/dialogs/ImagesPreviewDialog.vue'
import vehicleInsuranceMixin from '../../mixins/vehicleInsuranceMixin'
import FilesPreviewDialog from '@/global/components/dialogs/FilesViewerDialog.vue'
import { showDetailsAction, deleteAction, activationAction } from '@/global/components/table/helpers/tableActions'

const DEFAULT_EMPTY_VALUE = '--'
export default {
  name: 'VehicleInsurancesGridBlock',

  components: {
    FilesPreviewDialog,
    ImagesPreviewDialog,
    CellIcon,
    TableImageIconWithCounter,
    InformationDialog,
    TableRowActionsMenu,
    ActiveIcon,
    ImageField
  },

  mixins: [
    previewFiles,
    vehicleInsuranceMixin
  ],

  props: {
    data: {
      type: Array,
      default: () => ([])
    },
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    itemIndex: {
      type: Number,
      default: 0
    },
    blockConfig: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      slidedDown: false,
      informationDialogConfig: {
        openDialog: false,
        message: this.$t('fleet/vehicle.confirm_delete'),
        title: '',
        positiveBtnText: '',
        negativeBtnText: this.$t('base.cancel'),
        callbackFn: () => {}
      },
      documentEmailId: null,
      documentEmailFormData: {},
      documentEmailValidationErrors: {},
      documentValidationErrors: {},

      imagesPreviewDialogConfig: {
        show: false,
        title: this.$t('fleet/vehicle_comment.images'),
        field: {},
        images: [],
        removeApiConfig: {
          module: 'fleet',
          route: ''
        },
        removeCallback: (event) => this.insuranceRemoveImageCallback(event)
      },
      filesPreviewDialogConfig: {
        show: false,
        title: this.$t('fleet/vehicle.pdf_files'),
        field: {},
        files: [],
        removeApiConfig: {
          module: 'fleet',
          route: ''
        },
        removeCallback: (event) => this.insuranceRemovePdfCallback(event)
      },
      defaultEmptyValue: DEFAULT_EMPTY_VALUE
    }
  },

  computed: {
    actionsConfig () {
      return {
        tableConfig: {
          actionsConfig: [
            showDetailsAction(this.$t, (event) => this.onShowInsuranceDetails(event)),
            activationAction(this.$t, (event) => this.onInsuranceActivation(event), (event) => !!event?.data?.status),
            deleteAction(this.$t, (event) => this.onInsuranceDelete(event))
          ]
        }
      }
    }
  },

  methods: {
    formatUtcSqlDateTime (date, format) {
      return dateService.formatFromUtc(date, format)
    },

    getVisibleItemDetails () {
      return [
        {
          label: this.$t('fleet/vehicle_insurance.insurance_type'),
          value: this.item?.type?.description ? this.$t(this.item.type.description) : DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_insurance.contract_number'),
          value: this.item?.contract_number || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_insurance.foreign_id'),
          value: this.item?.foreign_id || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_insurance.franchise_details'),
          value: this.item?.franchise_details || DEFAULT_EMPTY_VALUE
        }
      ]
    },

    getAllItemDetails () {
      return [
        {
          label: this.$t('fleet/vehicle_insurance.other_details'),
          value: this.item?.other_details || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_insurance.start_date'),
          value: this.item?.start_date ? dateService.formatFromUtc(this.item.start_date, FORMATS.date) : DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_insurance.expiry_date'),
          value: this.item?.expiry_date ? dateService.formatFromUtc(this.item.expiry_date, FORMATS.date) : DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_insurance.duration'),
          value: this.item?.duration || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_insurance.payment_type'),
          value: this.item?.payment_type || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_insurance.monthly_instalment'),
          value: this.item?.monthly_instalment || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_insurance.currency'),
          value: this.item?.currency?.name || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_insurance.currency'),
          value: this.item?.currency?.name || DEFAULT_EMPTY_VALUE
        },
        {
          key: 'images',
          label: this.$t('fleet/vehicle_insurance.images'),
          value: this.item?.images || [],
          onClick: (event) => this.showImages(event, {
            module: 'fleet',
            route: 'vehicle-insurance/remove-image/'
          }, 'insurances')
        },
        {
          key: 'pdf',
          label: this.$t('fleet/vehicle_insurance.pdf'),
          value: this.item?.pdf || [],
          onClick: (event) => this.showPdfs(event, {
            module: 'fleet',
            route: 'vehicle-insurance/remove-pdf/'
          }, 'insurances')
        },
        {
          key: 'alarm_on',
          label: this.$t('fleet/vehicle_document.alarm_on'),
          value: this.item?.alarm_on,
          src: this.item?.alarm_on === 1 ? '/img/icons/green-check-circle.svg' : '/img/icons/grey-check-circle.svg'
        }
      ]
    }
  }
}
</script>

<style scoped lang="scss">
.block-title {
  display: flex;
  justify-content: flex-start;
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  color: #111827;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}

.block-title--wrapper {
  display: flex;
  height: 48px;
  align-items: center;
}

.data-label {
  font-family: 'Satoshi', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #6B7280;
}

.data-value {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #111827;
  text-align: right;
}

.arrowUpDown {
  position: absolute;
  bottom: 10px;
  left: 50%;
}

.arrowUpDown:hover {
  cursor: pointer;
}
</style>
